import React, { useState, useRef, useEffect } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "lift-ui";
import { PageHeader, Button, Input } from "lift-ui";
import { Search, Plus, GripVertical, RefreshCw, Trash2, X } from "lucide-react";
import { Badge } from "lift-ui";
import { useAuthContext } from "@lift-admin/utils/provider";
import Stations from "./stations";
import Pantry from "./pantry";
import "./pivot-content-kitchen.styles.scss";

interface StationsHandlers {
  onCreateStation: () => void;
  onUploadFiles: () => void;
  onReorder: () => void;
  searchTerm: string;
  onSearch: (term: string) => void;
}

interface PantryHandlers {
  onUploadClick: () => void;
  onReorder: () => void;
  searchTerm: string;
  onSearch: (term: string) => void;
  selectedCount: number;
  onBulkDelete: () => void;
  onClearSelection: () => void;
}

const PivotContentKitchen: React.FC = () => {
  const { canEdit, canManage, loading: authLoading } = useAuthContext();
  const hasEditAccess = !authLoading && canEdit("creative_mr_oven");
  const hasManageAccess = !authLoading && canManage("creative_mr_oven");

  const [activeTab, setActiveTab] = useState("stations");
  const [stationsHandlers, setStationsHandlers] = useState<StationsHandlers | null>(null);
  const [pantryHandlers, setPantryHandlers] = useState<PantryHandlers | null>(null);

  return (
    <div className="flex flex-col h-full overflow-hidden bg-white">
      <PageHeader
        title="Kitchen Buddies"
        renderCustomTop={
          <div className="flex items-center gap-3">
            {activeTab === "stations" && stationsHandlers ? (
              <>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Search stations..."
                    value={stationsHandlers.searchTerm}
                    onChange={(e) => stationsHandlers.onSearch(e.target.value)}
                    className="pl-10 w-80"
                  />
                </div>
                {hasEditAccess && (
                  <>
                    <Button
                      variant="default"
                      size="sm"
                      onClick={stationsHandlers.onCreateStation}
                      className="flex items-center gap-2 h-8 px-3"
                    >
                      <Plus className="h-4 w-4" />
                      Add Station
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={stationsHandlers.onReorder}
                      className="flex items-center gap-2 h-8 px-3"
                    >
                      <GripVertical className="h-4 w-4" />
                      Reorder Stations
                    </Button>
                  </>
                )}
              </>
            ) : activeTab === "pantry" && pantryHandlers ? (
              <>
                {pantryHandlers.selectedCount > 0 ? (
                  <>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs px-2 py-1 flex items-center gap-1">
                        {pantryHandlers.selectedCount} item{pantryHandlers.selectedCount !== 1 ? "s" : ""} selected
                        <button onClick={pantryHandlers.onClearSelection} className="ml-1 hover:text-foreground text-muted-foreground">
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    </div>
                    {hasManageAccess && (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={pantryHandlers.onBulkDelete}
                        className="flex items-center gap-2 h-8 px-3"
                      >
                        <Trash2 className="h-4 w-4" />
                        Delete Selected ({pantryHandlers.selectedCount})
                      </Button>
                    )}
                  </>
                ) : (
                  <>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        type="text"
                        placeholder="Search pantry..."
                        value={pantryHandlers.searchTerm}
                        onChange={(e) => pantryHandlers.onSearch(e.target.value)}
                        className="pl-10 w-80"
                      />
                    </div>
                    {hasEditAccess && (
                      <>
                        <Button
                          variant="default"
                          size="sm"
                          onClick={pantryHandlers.onUploadClick}
                          className="flex items-center gap-2 h-8 px-3"
                        >
                          <Plus className="h-4 w-4" />
                          Add Pantry
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={pantryHandlers.onReorder}
                          className="flex items-center gap-2 h-8 px-3"
                        >
                          <GripVertical className="h-4 w-4" />
                          Reorder Pantry
                        </Button>
                      </>
                    )}
                  </>
                )}
              </>
            ) : null}
          </div>
        }
      />

      <div className="flex-1 flex flex-col min-h-0 px-8 pt-6 pb-2">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
          <div className="mb-4">
            <TabsList className="grid grid-cols-2 w-fit">
              <TabsTrigger value="stations">Stations</TabsTrigger>
              <TabsTrigger value="pantry">Pantry</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="stations" className="flex-1 flex flex-col min-h-0 mt-0">
            <Stations onHandlersReady={setStationsHandlers} hideHeader hasEditAccess={hasEditAccess} hasManageAccess={hasManageAccess} />
          </TabsContent>
          <TabsContent value="pantry" className="flex-1 flex flex-col min-h-0 mt-0">
            <Pantry onHandlersReady={setPantryHandlers} hideHeader hasEditAccess={hasEditAccess} hasManageAccess={hasManageAccess} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default PivotContentKitchen;
